import { formatApiErrorDetail } from "@/lib/api";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { ShieldWarning, ArrowLeft, House, Info, Warning, X } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;

function loadPannellum() {
  if (window.pannellum) return Promise.resolve();
  return new Promise((res) => {
    if (!document.querySelector('link[data-pannellum]')) {
      const l = document.createElement("link");
      l.rel = "stylesheet"; l.dataset.pannellum = "1";
      l.href = "https://cdn.jsdelivr.net/npm/pannellum@2.5.6/build/pannellum.css";
      document.head.appendChild(l);
    }
    if (!document.querySelector('script[data-pannellum]')) {
      const s = document.createElement("script");
      s.src = "https://cdn.jsdelivr.net/npm/pannellum@2.5.6/build/pannellum.js";
      s.dataset.pannellum = "1"; s.onload = res;
      document.head.appendChild(s);
    } else res();
  });
}

export default function PublicEmergencyMap() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: "" });
  const [openTag, setOpenTag] = useState(null);
  const [techMode, setTechMode] = useState(false);
  const [techName, setTechName] = useState(() => localStorage.getItem("fr_tech_name") || "");

  const load = async (deepLinkTag) => {
    try {
      const r = await axios.get(`${API}/api/public/emergency/${token}`);
      setState({ loading: false, data: r.data, error: "" });
      // If a tag is currently open, refresh its snapshot from the fresh data.
      if (openTag) {
        const fresh = (r.data.tags || []).find(t => t.id === openTag.id);
        if (fresh) setOpenTag(fresh);
      }
      if (deepLinkTag) {
        const t = (r.data.tags || []).find(
          x => (x.short_id || "").toLowerCase() === deepLinkTag.toLowerCase() || x.id === deepLinkTag);
        if (t) setOpenTag(t);
      }
      return r.data;
    } catch (e) {
      setState({ loading: false, data: null, error: e.response?.data?.detail || "Invalid or expired link" });
      return null;
    }
  };

  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const want = (sp.get("tag") || "").trim();
    if (sp.get("tech") === "1") setTechMode(true);
    load(want);
  // eslint-disable-next-line
  }, [token]);

  const persistTechName = (v) => {
    setTechName(v);
    if (v.trim()) localStorage.setItem("fr_tech_name", v.trim());
  };

  if (state.loading) return <Shell><div className="text-white/60">Loading…</div></Shell>;
  if (state.error) return <Shell><div className="rugged-card p-8 text-center bg-white text-slate-900">
    <Warning size={40} className="text-red-500 mx-auto"/>
    <div className="mt-2 font-black">{state.error}</div>
    <div className="text-xs text-slate-500 mt-1">Ask your property manager for an updated Emergency Map link.</div>
  </div></Shell>;

  const d = state.data;
  const brand = d.branding || {};
  const appliedCount = (d.tags || []).filter(t => t.applied_at).length;
  return (
    <Shell brand={brand}>
      <Toaster position="top-right" richColors closeButton/>
      <div className="max-w-md mx-auto p-4 space-y-4" data-testid="public-emergency-map">
        <div className="text-white">
          <div className="text-[10px] font-black uppercase tracking-widest text-red-400 flex items-center gap-1">
            <ShieldWarning weight="fill" size={12}/> Emergency Map
          </div>
          <div className="font-black text-2xl mt-1 leading-tight">{d.property_address}{d.unit && ` · Unit ${d.unit}`}</div>
          <div className="text-xs text-white/60 mt-1">Tap any card to see the shut-off in the room and how to use it.</div>
        </div>

        {/* Tech Mode banner — reveals apply + photo controls per tag */}
        <div className={`p-3 border-2 ${techMode ? "border-emerald-500 bg-emerald-950/50" : "border-slate-700 bg-slate-900/60"}`}
             data-testid="tech-mode-panel">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="text-[10px] font-black uppercase tracking-widest text-emerald-400 flex items-center gap-1">
                🔧 {techMode ? "Tech Mode ON" : "On-site tech?"}
              </div>
              <div className="text-[11px] text-white/70 mt-0.5 leading-tight">
                {techMode
                  ? `${appliedCount}/${d.tags?.length || 0} tags marked applied. Tap a tag → snap photo → done.`
                  : "Turn this on to mark stickers applied and snap on-site photos."}
              </div>
            </div>
            <button onClick={() => setTechMode(v => !v)} data-testid="tech-mode-toggle"
              className={`shrink-0 px-3 h-9 font-black uppercase text-[10px] tracking-widest border-2 ${techMode ? "border-emerald-400 bg-emerald-500 text-white" : "border-slate-500 bg-slate-800 text-slate-200"}`}>
              {techMode ? "ON" : "Turn On"}
            </button>
          </div>
          {techMode && (
            <input type="text" value={techName} onChange={e => persistTechName(e.target.value)}
              placeholder="Your name (saved on this phone)"
              data-testid="tech-name-input"
              className="mt-2 w-full h-9 px-2 border-2 border-emerald-500 bg-white text-slate-900 font-bold text-sm"/>
          )}
        </div>

        {d.tags.length === 0 && !d.matterport_url && d.photos.length === 0 && (
          <div className="rugged-card p-6 bg-white text-center">
            <ShieldWarning size={40} className="text-slate-300 mx-auto"/>
            <div className="mt-2 font-black text-slate-700">No shut-offs mapped yet.</div>
            <div className="text-xs text-slate-500 mt-1">Ask your property manager to fill this out.</div>
          </div>
        )}

        {d.tags.length > 0 && (
          <div className="space-y-2">
            {d.tags.map(t => (
              <button key={t.id} onClick={() => setOpenTag(t)} data-testid={`public-tag-${t.id}`}
                className="w-full p-3 bg-white text-left border-l-4 flex items-start gap-3 hover:brightness-95 shadow-md relative"
                style={{ borderLeftColor: t.color || "#dc2626" }}>
                <div className="w-12 h-12 shrink-0 rounded-full flex items-center justify-center text-2xl"
                     style={{ backgroundColor: `${t.color || "#dc2626"}22`, color: t.color || "#dc2626" }}>
                  {t.icon || "📍"}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-black text-slate-900 text-base flex items-center gap-1.5">
                    {t.label}
                    {t.short_id && <span className="text-[9px] font-mono font-black text-slate-400">·{t.short_id}</span>}
                  </div>
                  {t.instruction_text && (
                    <div className="text-[12px] text-slate-600 leading-snug line-clamp-2">{t.instruction_text}</div>
                  )}
                  <div className="text-[9px] uppercase tracking-widest text-slate-400 mt-1 font-black">
                    {t.applied_at
                      ? <span className="text-emerald-600">✓ Applied{t.applied_by_name ? ` · ${t.applied_by_name}` : ""}</span>
                      : (t.photo_id ? "Tap to see in the room →" : "Tap for details →")}
                  </div>
                </div>
                {techMode && !t.applied_at && (
                  <span className="absolute top-2 right-2 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-amber-400 text-slate-900">
                    Not applied
                  </span>
                )}
              </button>
            ))}
          </div>
        )}

        {(d.matterport_url || d.photos.length > 0) && (
          <div className="text-white/70 text-[10px] uppercase tracking-widest font-black flex items-center gap-1 pt-2">
            <Info size={10}/> {d.matterport_url ? "3D tour available" : `${d.photos.length} reference photo${d.photos.length === 1 ? "" : "s"}`}
          </div>
        )}
      </div>

      {openTag && (
        <TagDetailModal tag={openTag} photos={d.photos} matterportUrl={d.matterport_url}
                        token={token}
                        techMode={techMode} techName={techName} setTechName={persistTechName}
                        onClose={() => setOpenTag(null)}
                        onChanged={async () => {
                          const fresh = await load();
                          if (fresh) {
                            const t = (fresh.tags || []).find(x => x.id === openTag.id);
                            if (t) setOpenTag(t);
                          }
                        }}/>
      )}

      <div className="max-w-md mx-auto text-white/50 text-[10px] text-center px-4 pt-6 pb-8" data-testid="public-footer-brand">
        <div className="flex items-center justify-center gap-2 flex-wrap"><span className="opacity-70">Secure magic link · Powered by</span><span className="inline-flex bg-white rounded px-1.5 py-0.5"><img src="/brand/fr-lockup.png?v=10" alt="FieldRadar.pro" className="h-4 w-auto"/></span></div>
      </div>
    </Shell>
  );
}

function TagDetailModal({ tag, photos, matterportUrl, token, techMode, techName, setTechName, onClose, onChanged }) {
  const photo = photos.find(p => p.id === tag.photo_id);
  const pannRef = useRef();
  const fileRef = useRef();
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!photo || photo.type !== "panorama_360" || !pannRef.current) return;
    let alive = true, viewer;
    loadPannellum().then(() => {
      if (!alive || !window.pannellum) return;
      viewer = window.pannellum.viewer(pannRef.current, {
        type: "equirectangular",
        panorama: `${API}/api/public/emergency/${token}/photo/${photo.id}`,
        autoLoad: true, showZoomCtrl: true,
        yaw: tag.yaw || 0, pitch: tag.pitch || 0, hfov: 90,
        hotSpots: [{ pitch: tag.pitch || 0, yaw: tag.yaw || 0, type: "info",
                     text: `${tag.icon || "📍"} ${tag.label}` }],
      });
    });
    return () => { alive = false; try { viewer?.destroy(); } catch { /* noop */ } };
  }, [photo, tag, token]);

  const applyOnly = async () => {
    const name = (techName || "").trim();
    if (!name) { toast.error("Enter your name first"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/api/public/emergency/${token}/tags/${tag.short_id || tag.id}/apply`,
        { tech_name: name, note: "" });
      toast.success(`Marked applied by ${name}`);
      await onChanged?.();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Failed")); }
    finally { setBusy(false); }
  };

  const applyWithPhoto = async (file) => {
    const name = (techName || "").trim();
    if (!name) { toast.error("Enter your name first"); return; }
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("tech_name", name);
      await axios.post(`${API}/api/public/emergency/${token}/tags/${tag.short_id || tag.id}/photo`,
        fd, { headers: { "Content-Type": "multipart/form-data" }});
      toast.success("Photo uploaded — tag applied");
      await onChanged?.();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Failed")); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/95 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4"
         onClick={onClose} data-testid="public-tag-modal">
      <div className="bg-white w-full max-w-lg overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="p-3 flex items-center justify-between" style={{ backgroundColor: tag.color || "#dc2626", color: "white" }}>
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-2xl">{tag.icon || "📍"}</span>
            <div className="min-w-0">
              <div className="font-black text-sm leading-tight">{tag.label}</div>
              <div className="text-[10px] uppercase tracking-widest opacity-80">
                {tag.category?.replace(/_/g, " ")}{tag.short_id ? ` · ${tag.short_id}` : ""}
              </div>
            </div>
          </div>
          <button onClick={onClose} className="w-9 h-9 flex items-center justify-center hover:bg-white/10">
            <X size={20}/>
          </button>
        </div>

        {photo ? (
          photo.type === "panorama_360" ? (
            <div ref={pannRef} className="w-full aspect-video bg-black"/>
          ) : (
            <div className="relative bg-black">
              <img src={`${API}/api/public/emergency/${token}/photo/${photo.id}`}
                   alt={tag.label} className="w-full block max-h-[60vh] object-contain" data-testid="public-tag-photo"/>
              {(tag.x_pct || tag.y_pct) && (
                <div style={{ left: `${tag.x_pct}%`, top: `${tag.y_pct}%`, backgroundColor: tag.color || "#dc2626" }}
                  className="absolute -translate-x-1/2 -translate-y-1/2 w-14 h-14 rounded-full flex items-center justify-center text-3xl border-4 border-white shadow-2xl animate-pulse">
                  {tag.icon || "📍"}
                </div>
              )}
            </div>
          )
        ) : matterportUrl ? (
          <div className="aspect-video bg-black">
            <iframe src={matterportUrl} title="3D tour" allowFullScreen className="w-full h-full border-0"/>
          </div>
        ) : null}

        <div className="p-4 space-y-2">
          {tag.instruction_text ? (
            <>
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">How to use</div>
              <p className="text-sm text-slate-800 leading-relaxed">{tag.instruction_text}</p>
            </>
          ) : (
            <p className="text-sm text-slate-500 italic">No instruction saved. If you can't see the shut-off, contact your property manager.</p>
          )}

          {tag.applied_at && (
            <div className="text-[11px] bg-emerald-50 border border-emerald-300 text-emerald-800 p-2 font-bold" data-testid="applied-status">
              ✓ Applied on-site{tag.applied_by_name ? ` by ${tag.applied_by_name}` : ""}
              {tag.applied_at && ` · ${new Date(tag.applied_at).toLocaleDateString()}`}
            </div>
          )}

          {techMode && (
            <div className="mt-3 p-3 border-2 border-emerald-500 bg-emerald-50 space-y-2" data-testid="tech-actions">
              <div className="text-[10px] font-black uppercase tracking-widest text-emerald-800">
                🔧 On-site tech actions
              </div>
              {!techName?.trim() && (
                <input type="text" value={techName || ""} onChange={e => setTechName?.(e.target.value)}
                  placeholder="Your name (required)"
                  data-testid="modal-tech-name"
                  className="w-full h-10 px-2 border-2 border-emerald-500 bg-white font-bold text-sm"/>
              )}
              <div className="grid grid-cols-2 gap-2">
                <button onClick={applyOnly} disabled={busy || !techName?.trim()}
                  data-testid="apply-only-btn"
                  className="h-11 border-2 border-emerald-700 bg-white text-emerald-800 font-black uppercase tracking-widest text-[10px] disabled:opacity-50">
                  {tag.applied_at ? "✓ Re-mark" : "Mark Applied"}
                </button>
                <label className={`h-11 border-2 border-emerald-700 bg-emerald-600 text-white font-black uppercase tracking-widest text-[10px] flex items-center justify-center cursor-pointer ${busy || !techName?.trim() ? "opacity-50 pointer-events-none" : ""}`}
                       data-testid="apply-photo-btn">
                  📸 Snap + Apply
                  <input ref={fileRef} type="file" accept="image/*" capture="environment"
                    onChange={e => applyWithPhoto(e.target.files?.[0])} disabled={busy || !techName?.trim()}
                    className="hidden" data-testid="apply-photo-input"/>
                </label>
              </div>
              {busy && <div className="text-[10px] text-emerald-800 font-bold">Uploading…</div>}
            </div>
          )}

          <button onClick={onClose} className="w-full h-11 mt-2 bg-slate-900 text-white font-black uppercase tracking-widest text-sm">
            Back to list
          </button>
        </div>
      </div>
    </div>
  );
}

function Shell({ children, brand }) {
  return (
    <div className="min-h-screen" style={{ backgroundColor: "#0b1220" }}>
      <div className="bg-red-700 text-white text-center py-2 text-[10px] font-black uppercase tracking-widest">
        ⚠️ If someone is hurt or you smell gas — call 911 first
      </div>
      <div className="max-w-md mx-auto flex items-center gap-3 p-4">
        {brand?.logo_path ? (
          <img src={`${API}/api/files/${brand.logo_path}`} alt={brand.name || ""} className="h-10 max-w-[100px] object-contain"/>
        ) : (
          <House weight="fill" size={28} className="text-red-500"/>
        )}
        <div>
          <div className="text-[9px] font-black uppercase tracking-widest text-red-400">Emergency Map</div>
          <div className="text-white font-black text-lg">{brand?.name || "Property Management"}</div>
        </div>
      </div>
      {children}
    </div>
  );
}
